import { useMemo, useState, useEffect, useRef } from 'react';
import { calcularAptidaoCarteira } from '@/lib/carteiraClaro/calcularAptidaoCarteira';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Users, Phone, Mail, Building2, CreditCard, MapPin, Calendar,
  TrendingUp, AlertTriangle, AlertCircle, Check, ChevronDown,
  Smartphone, PhoneCall, MessageSquare, DollarSign,
  Signal, Clock, UserCheck, Wifi, ChevronRight, Hash,
  Save, Pencil, Upload, Send, Download, FileText
} from 'lucide-react';
import { formatarCNPJ, formatarMoeda, formatarTelefone, formatarCPF } from '@/utils/formatters';
import { ClickToCall } from '@/components/common/ClickToCall';
import { useLeadDadosComplementar } from '@/hooks/useLeadDadosComplementar';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { invokeWithRealError } from '@/lib/invokeWithRealError';
import type { LeadCarteira } from '@/hooks/useLeadsCarteira';
import { BadgeStatusAptidao } from './BadgeStatusAptidao';
import { SolicitarFaturaModal } from '@/components/faturas/SolicitarFaturaModal';
import { useFaturaManual } from '@/hooks/useFaturaManual';
import { useAbrirConversaInbox } from '@/hooks/useAbrirConversaInbox';
import { toast } from 'sonner';
import { useLinhasOperadoraCnpj, buildOperadoraMap, type LinhaOperadora } from '@/hooks/useLinhasOperadoraCnpj';
import { formatOperadora } from '@/lib/operadoras';
import { TelefonesEncontradosCard } from './LeadDadosComplementarTab';

interface LeadPerfilInteligenteProps {
  lead: LeadCarteira;
  onLigar: (telefone: string) => void;
  campanhaNome?: string | null;
}

// GB nominal mínimo para considerar a linha um PLANO DE DADOS.
// Abaixo disso é plano de VOZ / IoT (ex.: "Internet Corp 100MB" = 0,1 GB) —
// onde "defasado por GB<15" e "linha estourando" NÃO fazem sentido.
const GB_PLANO_DADOS_MIN = 1;

// === Helper: extrair sócios dos dados complementares ===
interface SocioInfo {
  nome: string;
  cargo?: string;
  cpf?: string;
  participacao?: string;
  telefones: string[];
  whatsapp: boolean;
  cnpj_socio?: string;
  emails: string[];
}

function extrairSocios(dc: any): SocioInfo[] {
  if (!dc) return [];
  const socios: SocioInfo[] = [];

  for (let i = 1; i <= 4; i++) {
    const nome = dc[`nome_socio${i}`];
    if (!nome) continue;

    const telefones: string[] = [];
    for (let t = 1; t <= 3; t++) {
      const tel = dc[`telefone${t}_socio${i}`];
      if (tel) telefones.push(tel);
    }

    socios.push({
      nome,
      cargo: dc[`cargo_socio${i}`],
      cpf: dc[`cpf_socio${i}`],
      participacao: dc[`participacao_socio${i}`],
      telefones,
      whatsapp: !!dc[`whatsapp_socio${i}`],
      cnpj_socio: dc[`cnpj_socio${i}`],
      emails: [],
    });
  }

  // Ordenar: Administrador primeiro, depois quem tem telefone
  socios.sort((a, b) => {
    const aAdmin = a.cargo?.toLowerCase()?.includes('administrador') ? 1 : 0;
    const bAdmin = b.cargo?.toLowerCase()?.includes('administrador') ? 1 : 0;
    if (bAdmin !== aAdmin) return bAdmin - aAdmin;
    return b.telefones.length - a.telefones.length;
  });

  return socios;
}

// === Helper: telefones da empresa ===
function extrairTelefonesEmpresa(dc: any): { numero: string; tipo: string; whatsapp?: boolean }[] {
  if (!dc) return [];
  const tels: { numero: string; tipo: string; whatsapp?: boolean }[] = [];

  for (let i = 1; i <= 4; i++) {
    if (dc[`celular${i}`]) tels.push({ numero: dc[`celular${i}`], tipo: `Celular ${i}`, whatsapp: dc[`whatsapp${i}`] });
  }
  for (let i = 1; i <= 4; i++) {
    if (dc[`fixo${i}`]) tels.push({ numero: dc[`fixo${i}`], tipo: `Fixo ${i}` });
  }
  if (dc.telefone_sistema) tels.push({ numero: dc.telefone_sistema, tipo: 'Tel. Sistema' });

  return tels;
}

// === Componente de Telefone com ações ===
function TelefoneAction({ numero, leadId, label, whatsapp, onLigar, operadoraInfo }: { numero: string; leadId: string; label?: string; whatsapp?: boolean; onLigar: (t: string) => void; operadoraInfo?: LinhaOperadora | null }) {
  // Abre o WhatsApp no inbox interno (Evolution) com a conexão do usuário logado
  const { abrirConversa } = useAbrirConversaInbox();

  return (
    <div className="flex items-center gap-1.5">
      {label && <span className="text-xs text-muted-foreground min-w-[70px]">{label}</span>}
      <ClickToCall telefone={numero} leadId={leadId}>
        <span className="text-sm text-primary hover:underline cursor-pointer flex items-center gap-1 font-medium">
          {formatarTelefone(numero)}
          <PhoneCall className="w-3 h-3" />
        </span>
      </ClickToCall>
      {operadoraInfo?.operadora && (
        <span className="rounded-full border border-border bg-muted/40 px-1.5 py-px text-[8.5px] font-bold uppercase tracking-wider leading-none text-muted-foreground">
          {formatOperadora(operadoraInfo.operadora)}
        </span>
      )}
      {whatsapp && (
        <button type="button" onClick={() => abrirConversa(numero)} className="text-success hover:text-success">
          <MessageSquare className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

export function LeadPerfilInteligente({ lead, onLigar, campanhaNome }: LeadPerfilInteligenteProps) {
  const cnpjDigits = lead.cnpj?.replace(/\D/g, '') || '';
  const { data: dc, isLoading: loadingDC } = useLeadDadosComplementar(lead.cnpj);
  const { data: linhasOperadora = [] } = useLinhasOperadoraCnpj(lead.cnpj);
  const operadoraMap = useMemo(() => buildOperadoraMap(linhasOperadora), [linhasOperadora]);
  const queryClient = useQueryClient();
  const [linhasExpandidas, setLinhasExpandidas] = useState(false);
  const [empresaAberta, setEmpresaAberta] = useState(true);
  const [linhaEditando, setLinhaEditando] = useState('');
  const [salvandoLinha, setSalvandoLinha] = useState(false);
  const [editandoLinha, setEditandoLinha] = useState(false);
  const [linhaInicializada, setLinhaInicializada] = useState(false);
  const [modalSolicitarManual, setModalSolicitarManual] = useState(false);
  const [solicitarFaturaDialogOpen, setSolicitarFaturaDialogOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Hook para upload direto de fatura
  const { uploadAndProcess, isLoading: isUploadingDireto } = useFaturaManual({
    cnpj: lead.cnpj || '',
    clienteId: lead.cliente_id,
    razaoSocial: lead.razao_social || undefined,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fatura-resumo-cnpj', cnpjDigits] });
      queryClient.invalidateQueries({ queryKey: ['fatura-linhas-cnpj', cnpjDigits] });
      queryClient.invalidateQueries({ queryKey: ['cliente-linha-master-perfil', cnpjDigits] });
      toast.success('Fatura importada! A análise será processada automaticamente.');
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadAndProcess(file);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };
  const { data: faturaResumo } = useQuery({
    queryKey: ['fatura-resumo-cnpj', cnpjDigits],
    queryFn: async () => {
      if (!cnpjDigits) return null;
      const { data } = await supabase
        .from('v_base_fatura_ultima_por_cnpj')
        .select('*')
        .eq('cnpj_digits', cnpjDigits)
        .maybeSingle();
      return data;
    },
    enabled: !!cnpjDigits,
    staleTime: 5 * 60 * 1000,
  });

  // Buscar URL do PDF da fatura mais recente
  const { data: faturaArquivo } = useQuery({
    queryKey: ['fatura-arquivo-url', cnpjDigits],
    queryFn: async () => {
      if (!cnpjDigits) return null;
      const { data } = await supabase
        .from('fatura_upload')
        .select('arquivo_url')
        .eq('cnpj_fatura', cnpjDigits)
        .not('arquivo_url', 'is', null)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      return data;
    },
    enabled: !!cnpjDigits,
  });

  // Buscar linhas ativas
  const { data: linhasAtivas } = useQuery({
    queryKey: ['fatura-linhas-cnpj', cnpjDigits],
    queryFn: async () => {
      if (!cnpjDigits) return [];
      const { data } = await supabase
        .from('v_base_fatura_linhas_ativas')
        .select('*')
        .eq('cnpj_digits', cnpjDigits);
      return data || [];
    },
    enabled: !!cnpjDigits,
    staleTime: 5 * 60 * 1000,
  });

  // Buscar linha do MASTER (clientes.linha_conta_fatura)
  const { data: clienteMaster, isLoading: isLoadingMaster } = useQuery({
    queryKey: ['cliente-linha-master-perfil', cnpjDigits],
    queryFn: async () => {
      if (!cnpjDigits) return null;
      const { data } = await supabase
        .from('clientes')
        .select('id, linha_conta_fatura')
        .eq('cnpj', cnpjDigits)
        .maybeSingle();
      return data;
    },
    enabled: !!cnpjDigits,
  });

  const linhaMaster = clienteMaster?.linha_conta_fatura?.replace(/\D/g, '') || '';
  const linhaContaOp = (lead as any).conta_operadora?.replace(/\D/g, '') || '';
  const melhorLinha = (linhaMaster && linhaMaster.length === 11 ? linhaMaster : null) ||
    (linhaContaOp && linhaContaOp.length === 11 ? linhaContaOp : null) || '';

  // Inicializar input da linha
  useEffect(() => {
    if (!linhaInicializada && !isLoadingMaster) {
      setLinhaEditando(melhorLinha);
      setLinhaInicializada(true);
    }
  }, [melhorLinha, linhaInicializada, isLoadingMaster]);

  const linhaSalva = melhorLinha.length === 11;

  // Salvar linha no MASTER
  const salvarLinha = async () => {
    const linhaLimpa = linhaEditando.replace(/\D/g, '');
    if (linhaLimpa && linhaLimpa.length !== 11) {
      toast.error('Linha deve ter 11 dígitos (DDD + número)');
      return;
    }
    setSalvandoLinha(true);
    try {
      if (cnpjDigits) {
        // FONTE DA VERDADE: grava na carteira (leads_carteira). Um gatilho do banco
        // (sync_linha_leads_to_cliente) sincroniza a linha p/ o master `clientes`
        // automaticamente, então fica disponível em todos os lugares que vinculam o CNPJ.
        const { error } = await supabase
          .from('leads_carteira')
          .update({
            linha_conta_fatura: linhaLimpa || null,
            conta_operadora: linhaLimpa || null
          })
          .eq('cnpj', cnpjDigits);
        if (error) throw error;
        // Best-effort no master direto (gestor consegue; p/ consultor o gatilho já cobriu).
        // Ignora erro de RLS aqui — a carteira + gatilho já garantiram a gravação.
        await supabase
          .from('clientes')
          .update({ linha_conta_fatura: linhaLimpa || null })
          .eq('cnpj', cnpjDigits);
      }
      queryClient.invalidateQueries({ queryKey: ['cliente-linha-master-perfil', cnpjDigits] });
      queryClient.invalidateQueries({ queryKey: ['gestao-leads'] });
      queryClient.invalidateQueries({ queryKey: ['gestao-carteira'] });
      toast.success('Linha salva no cadastro do cliente!');
      setEditandoLinha(false);
    } catch (error: any) {
      toast.error(error.message || 'Erro ao salvar linha');
    } finally {
      setSalvandoLinha(false);
    }
  };

  // Formatar linha telefônica
  const formatarLinha = (linha: string): string => {
    const digits = linha.replace(/\D/g, '');
    if (digits.length === 11) return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    return linha;
  };

  // Handler para solicitação automática Bee360
  const handleSolicitarBee360 = async () => {
    const linhaLimpa = linhaEditando.replace(/\D/g, '');
    if (!linhaLimpa || linhaLimpa.length !== 11) {
      toast.error('Salve uma linha válida (11 dígitos) antes de solicitar');
      return;
    }

    // Auto-salvar se linha foi editada mas não salva
    if (linhaLimpa !== melhorLinha) {
      await salvarLinha();
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      // Helper extrai motivo REAL quando edge function retorna non-2xx
      const { data, error, mensagem } = await invokeWithRealError('solicitar-fatura', {
        body: {
          cliente_id: lead.cliente_id || undefined,
          cnpj: lead.cnpj,
          linha_plano: linhaLimpa,
          linha_solicitante: user?.phone || linhaLimpa,
          solicitante_id: user?.id,
        }
      });
      if (error) throw new Error(mensagem || 'Erro desconhecido');
      // edge function agora retorna 200 + ok:false quando n8n falha — checar
      if (data && (data as any).ok === false) throw new Error((data as any).mensagem || (data as any).error || 'Falha ao solicitar fatura');
      queryClient.invalidateQueries({ queryKey: ['todas-faturas'] });
      toast.success('Fatura solicitada via robô! Aparecerá na página Faturas em instantes.');
    } catch (err: any) {
      toast.error(err.message || 'Erro ao solicitar fatura');
    }
  };

  const socios = useMemo(() => extrairSocios(dc), [dc]);
  const telefonesEmpresa = useMemo(() => extrairTelefonesEmpresa(dc), [dc]);

  // Cálculos de renovação
  const analise = useMemo(() => {
    const totalLinhas = faturaResumo?.total_linhas_ativas || linhasAtivas?.length || 0;
    const valorTotal = faturaResumo?.valor_total_a_pagar || 0;
    const gbTotal = faturaResumo?.gb_total_contratado || 0;
    // Para RENOVACAO_CLARO: dados da planilha têm prioridade máxima (são absolutos e atuais)
    const isRenovacaoClaro = lead.tipo_lead === 'RENOVACAO_CLARO';
    const mesesContrato = (isRenovacaoClaro && lead.tempo_contrato_voz_num)
      ? lead.tempo_contrato_voz_num
      : (dc?.meses_contrato || lead.tempo_contrato_voz_num || 0);
    const valorMedioLinha = totalLinhas > 0 ? valorTotal / totalLinhas : 0;
    const gbMedioLinha = totalLinhas > 0 ? gbTotal / totalLinhas : 0;
    // "Defasado por GB" só vale para plano de DADOS (>= 1 GB nominal).
    // Plano de voz (ex.: 0,1 GB) não é defasado por GB — evita falso positivo.
    const ehPlanoDados = gbMedioLinha >= GB_PLANO_DADOS_MIN;
    const planoDefasado = ehPlanoDados && gbMedioLinha < 15;

    // Calcular M atual
    // Para RENOVACAO_CLARO: o valor importado já é o tempo absoluto atual — não somar delta
    let mAtual = mesesContrato;
    if (mesesContrato > 0 && !isRenovacaoClaro) {
      // Se temos data de enriquecimento ou data da fatura, calcular meses decorridos
      const dataRef = dc?.data_enriquecimento || faturaResumo?.periodo_inicio;
      if (dataRef) {
        const diff = Math.floor((Date.now() - new Date(dataRef).getTime()) / (1000 * 60 * 60 * 24 * 30));
        mAtual = mesesContrato + Math.max(0, diff);
      }
    }

    const classificacao = String((lead as { classificacao?: string | null }).classificacao || dc?.classificacao || '').trim().toUpperCase();
    const aptidao = calcularAptidaoCarteira(mAtual, classificacao);
    const aptoRenovacao = aptidao === 'APTO';
    const mesApto = aptidao === 'NAO_APTO' ? 12 : classificacao === 'CG' ? 15 : 17;
    const mesesParaRenovacao = Math.max(0, mesApto - mAtual);

    // Subutilização
    let linhasSubutilizadas = 0;
    let linhasEstourando = 0;
    linhasAtivas?.forEach(l => {
      // Só conta em plano de DADOS (gb nominal real). Ignora voz/IoT,
      // onde o consumo "estoura" matematicamente sobre uma franquia minúscula.
      if (l.gb_nominal && l.gb_nominal >= GB_PLANO_DADOS_MIN && l.gb_utilizados !== null && l.gb_utilizados !== undefined) {
        const pct = l.gb_utilizados / l.gb_nominal;
        if (pct < 0.3) linhasSubutilizadas++;
        if (pct > 1.0) linhasEstourando++;
      }
    });

    return {
      totalLinhas, valorTotal, gbTotal, mesesContrato, mAtual,
      valorMedioLinha, gbMedioLinha, planoDefasado,
      aptoRenovacao, mesesParaRenovacao, mesApto, aptidao,
      linhasSubutilizadas, linhasEstourando
    };
  }, [dc, faturaResumo, linhasAtivas, lead]);

  // A análise de renovação só faz sentido para lead de RENOVAÇÃO COM dado de carteira.
  // Sem carteira (tempo em branco) ou outro tipo de lead (tabulação, portabilidade…) → não mostra.
  const mostrarRenovacao = lead.tipo_lead === 'RENOVACAO_CLARO' && lead.status_aptidao !== 'PROSPECT';

  const { abrirConversa } = useAbrirConversaInbox();
  const fmtCompetencia = (v?: string | null) => {
    const m = String(v || '').match(/^(\d{4})-(\d{2})/);
    if (!m) return v || '—';
    const meses = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
    return `${meses[Number(m[2]) - 1] || m[2]}/${m[1]}`;
  };
  const FILETE = 'absolute inset-y-0 left-0 w-1 bg-[linear-gradient(180deg,#6E1626,#4E1522_55%,#3E0E1B)]';
  const HEADER38 = 'flex min-h-[38px] flex-wrap items-center gap-2 border-b border-border px-4 py-1';
  const TITULO = 'flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.09em] text-foreground';
  const TILE = 'rounded-lg border border-border bg-muted/40 px-3 py-2';
  const TILE_NUM = 'truncate font-display text-[20px] font-bold leading-tight tabular-nums';
  const TILE_ROT = 'text-[9px] font-bold uppercase tracking-[0.08em] text-muted-foreground';
  const temFatura = Boolean(faturaResumo) || (linhasAtivas?.length || 0) > 0;
  const decisor = socios[0];
  const demaisSocios = socios.slice(1);

  return (
    <div className="space-y-4">
      {/* ═══ CAIXA 1 · ANÁLISE DE RENOVAÇÃO (polimórfica — variante renovação) ═══ */}
      {mostrarRenovacao && (
        <div className="relative overflow-hidden rounded-xl border border-border bg-card pl-1">
          <span aria-hidden className={FILETE} />
          <div className={HEADER38}>
            <p className={TITULO}><TrendingUp className="h-3.5 w-3.5 text-muted-foreground" />Análise de renovação</p>
            <span className="min-w-0 flex-1" />
            {campanhaNome && (
              <span className="max-w-[200px] truncate rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-muted-foreground" title={campanhaNome}>{campanhaNome}</span>
            )}
            <span className={`rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${analise.aptoRenovacao ? 'border-[#075E54]/40 bg-[#075E54]/[0.06] text-[#075E54] dark:text-[#25D366]' : 'border-primary/40 bg-primary/5 text-primary dark:text-[#C87484]'}`}>
              {analise.aptoRenovacao ? 'Apto · renovação' : analise.aptidao === 'APTO_COM_APARELHO' ? 'Apto com aparelho · renovação' : 'Não apto · renovação'}
            </span>
          </div>
          <div className="space-y-3 p-4">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-5">
              <div className={TILE}>
                <p className={`${TILE_NUM} ${analise.aptoRenovacao ? 'text-[#075E54] dark:text-[#25D366]' : 'text-primary dark:text-[#C87484]'}`}>M{analise.mAtual}</p>
                <p className={TILE_ROT}>{analise.aptoRenovacao ? 'já apto' : analise.aptidao === 'APTO_COM_APARELHO' ? `com aparelho · sem aparelho em M${analise.mesApto}` : `não apto · com aparelho em M12 · faltam ${analise.mesesParaRenovacao}`}</p>
              </div>
              <div className={TILE}>
                <p className={`${TILE_NUM} text-money-highlight`}>{analise.valorTotal > 0 ? formatarMoeda(analise.valorTotal) : '—'}</p>
                <p className={TILE_ROT}>valor / conta</p>
              </div>
              <div className={TILE}>
                <p className={`${TILE_NUM} text-foreground`}>{temFatura ? analise.linhasSubutilizadas : '—'}</p>
                <p className={TILE_ROT}>linhas subutilizadas</p>
              </div>
              <div className={TILE}>
                <p className={`${TILE_NUM} ${analise.linhasEstourando > 0 ? 'text-primary dark:text-[#C87484]' : 'text-foreground'}`}>{temFatura ? analise.linhasEstourando : '—'}</p>
                <p className={TILE_ROT}>linha{analise.linhasEstourando === 1 ? '' : 's'} estourando</p>
              </div>
              <div className={TILE}>
                <p className={`${TILE_NUM} text-foreground`}>{analise.totalLinhas || '—'}</p>
                <p className={TILE_ROT}>linhas na conta</p>
              </div>
            </div>

            {/* mini-tabela Voz · B.Larga · Telemetria — — para o que não veio */}
            <div className="grid grid-cols-[minmax(0,1fr)_72px_72px_84px] items-center gap-2 text-[12px]">
              <span />
              <span className="text-right text-[8.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Voz</span>
              <span className="text-right text-[8.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground">B.Larga</span>
              <span className="text-right text-[8.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Telemetria</span>
              <span className="border-t border-border pt-1 text-muted-foreground">Tempo de contrato (M)</span>
              <span className="border-t border-border pt-1 text-right font-semibold tabular-nums text-foreground">{lead.tempo_contrato_voz_num ?? '—'}</span>
              <span className="border-t border-border pt-1 text-right tabular-nums text-muted-foreground">{lead.tempo_contrato_bl ?? '—'}</span>
              <span className="border-t border-border pt-1 text-right tabular-nums text-muted-foreground">—</span>
              <span className="text-muted-foreground">Quantidade de linhas</span>
              <span className="text-right font-semibold tabular-nums text-foreground">{lead.qtd_linhas_voz ?? '—'}</span>
              <span className="text-right tabular-nums text-muted-foreground">{lead.qtd_linhas_banda_larga ?? '—'}</span>
              <span className="text-right tabular-nums text-muted-foreground">{lead.qtd_linhas_telemetria ?? '—'}</span>
            </div>

            <div className="flex flex-wrap items-center gap-2 text-[12px]">
              {(dc?.territorio || (lead as any).territorio) && (
                <span className="rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Território {dc?.territorio || (lead as any).territorio}</span>
              )}
              {dc?.classificacao && (
                <span className="rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-muted-foreground">Classificação {dc.classificacao}</span>
              )}
              {dc?.nome_adm && (
                <span className="text-muted-foreground">ADM <b className="font-bold text-foreground">{dc.nome_adm}</b>{dc.telefone_sistema ? <span className="tabular-nums"> · {formatarTelefone(dc.telefone_sistema)}</span> : null}</span>
              )}
            </div>
          </div>
          <div className="flex min-h-[26px] flex-wrap items-center justify-between gap-2 border-t border-border bg-[#FAF8F4] px-4 py-1 text-[10px] text-muted-foreground dark:bg-[#241A1A]">
            <span>subutilizada = menos de 30% da franquia · estourando = acima de 100%</span>
            <span>aptidão pela régua da operadora</span>
          </div>
        </div>
      )}

      {/* ═══ CAIXA 2 · CONTA CLARO — FATURA ANALISADA (só renovação com fatura) ═══ */}
      {lead.tipo_lead === 'RENOVACAO_CLARO' && temFatura && (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <div className={HEADER38}>
            <p className={TITULO}><FileText className="h-3.5 w-3.5 text-muted-foreground" />Conta Claro — fatura analisada</p>
            <span className="text-[10.5px] text-muted-foreground">· lida pela IA{faturaResumo?.competencia ? ` · competência ${fmtCompetencia(faturaResumo.competencia)}` : ''}</span>
            <span className="min-w-0 flex-1" />
            {editandoLinha ? (
              <span className="flex items-center gap-1.5">
                <Input value={linhaEditando} onChange={(e) => setLinhaEditando(e.target.value)} placeholder="51999887766" className="h-7 w-[136px] text-xs tabular-nums" maxLength={15} />
                <Button size="sm" className="h-7 gap-1 border-0 bg-[linear-gradient(90deg,#4A1320,#5B0A0D)] px-2 text-[11px] font-bold text-white hover:brightness-110 dark:bg-[linear-gradient(90deg,#9D2530,#6C2236)]" onClick={salvarLinha} disabled={salvandoLinha}>
                  {salvandoLinha ? '…' : 'Salvar'}
                </Button>
              </span>
            ) : (
              <button type="button" onClick={() => setEditandoLinha(true)}
                className={`flex h-7 items-center gap-1.5 rounded-full border px-2.5 text-[11px] font-semibold tabular-nums transition-colors hover:border-primary/40 ${linhaSalva ? 'border-border bg-muted/40 text-foreground' : 'border-primary/40 bg-primary/5 text-primary'}`}
                title={linhaSalva ? 'Linha usada para pedir a fatura — clique para trocar' : 'Nenhuma linha salva — clique para cadastrar'}>
                {linhaSalva ? <><Check className="h-3 w-3 text-[#075E54] dark:text-[#25D366]" />{formatarLinha(melhorLinha)}</> : 'definir linha da conta'}
              </button>
            )}
            <Button variant="outline" size="sm" className="h-7 px-2 text-[11px]" onClick={handleSolicitarBee360}>Reenviar</Button>
            <Button variant="outline" size="sm" className="h-7 px-2 text-[11px]" onClick={() => fileInputRef.current?.click()} disabled={isUploadingDireto}>Substituir</Button>
            {faturaArquivo?.arquivo_url && (
              <Button variant="outline" size="sm" className="h-7 px-2 text-[11px]" onClick={() => window.open(faturaArquivo.arquivo_url!, '_blank')}><Download className="mr-1 h-3 w-3" />Baixar PDF</Button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 p-4 xl:grid-cols-4">
            <div className={TILE}><p className={`${TILE_NUM} text-foreground`}>{analise.totalLinhas || '—'}</p><p className={TILE_ROT}>linhas ativas</p></div>
            <div className={TILE}><p className={`${TILE_NUM} text-money-highlight`}>{analise.valorTotal > 0 ? formatarMoeda(analise.valorTotal) : '—'}</p><p className={TILE_ROT}>valor total</p></div>
            <div className={TILE}><p className={`${TILE_NUM} text-foreground`}>{analise.gbTotal > 0 ? `${analise.gbTotal} GB` : '—'}</p><p className={TILE_ROT}>contratado</p></div>
            <div className={TILE}><p className={`${TILE_NUM} text-foreground`}>{fmtCompetencia(faturaResumo?.competencia)}</p><p className={TILE_ROT}>competência</p></div>
          </div>
          {(linhasAtivas?.length || 0) > 0 && (
            <div className="border-t border-border">
              <button type="button" onClick={() => setLinhasExpandidas(!linhasExpandidas)} className="flex w-full items-center gap-1.5 px-4 py-2 text-[12px] font-semibold text-foreground transition-colors hover:bg-muted/40">
                {linhasExpandidas ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                Detalhe por linha <span className="font-normal text-muted-foreground">· {linhasAtivas!.length} linha{linhasAtivas!.length === 1 ? '' : 's'}</span>
              </button>
              {linhasExpandidas && (
                <div className="px-4 pb-3">
                  {linhasAtivas!.map((l: any, i: number) => (
                    <div key={l.msisdn || i} className="flex items-baseline gap-2 border-b border-border py-1.5 text-[12px] last:border-b-0">
                      <span className="flex-shrink-0 font-bold tabular-nums text-foreground">{formatarTelefone(l.msisdn || '')}</span>
                      <span className="min-w-0 truncate text-muted-foreground" title={l.plano_atual || ''}>{l.plano_atual || '—'}</span>
                      <span className="ml-auto flex-shrink-0 tabular-nums text-muted-foreground">{l.gb_nominal ? `${l.gb_nominal} GB` : '—'}</span>
                      <span className="w-[80px] flex-shrink-0 text-right tabular-nums text-foreground">{l.valor_mensal_linha ? formatarMoeda(l.valor_mensal_linha) : '—'}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ═══ CAIXA 3 · SÓCIOS E DECISORES ═══ */}
      {socios.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <div className={HEADER38}>
            <p className={TITULO}><Users className="h-3.5 w-3.5 text-muted-foreground" />Sócios e decisores</p>
            <span className="text-[10.5px] text-muted-foreground">· {socios.length} no quadro</span>
          </div>
          <div className="grid gap-3 p-4 md:grid-cols-2">
            {decisor && (
              <div className="relative overflow-hidden rounded-lg border border-border bg-muted/30 py-2.5 pl-3.5 pr-3">
                <span aria-hidden className={FILETE.replace('w-1', 'w-[3px]')} />
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-[13px] font-bold uppercase text-foreground">{decisor.nome}</p>
                  <span className="rounded-full bg-[linear-gradient(90deg,#4A1320,#5B0A0D)] px-2 py-0.5 text-[8.5px] font-bold uppercase tracking-wider text-white dark:bg-[linear-gradient(90deg,#9D2530,#6C2236)]">Decisor provável</span>
                </div>
                <p className="mt-0.5 text-[11px] text-muted-foreground">{decisor.cargo || 'Sócio'}{decisor.cpf ? ` · CPF ${formatarCPF(decisor.cpf)}` : ''}</p>
                {decisor.telefones.length > 0 ? (
                  <div className="mt-1.5 flex flex-wrap items-center gap-2">
                    <span className="whitespace-nowrap text-[13.5px] font-bold tabular-nums text-foreground">{formatarTelefone(decisor.telefones[0])}</span>
                    {operadoraMap.get(decisor.telefones[0]?.replace(/\D/g, '').slice(-11))?.operadora && (
                      <span className="rounded-full border border-border bg-card px-1.5 py-px text-[8.5px] font-bold uppercase tracking-wider text-muted-foreground">
                        {formatOperadora(operadoraMap.get(decisor.telefones[0].replace(/\D/g, '').slice(-11))!.operadora)}
                      </span>
                    )}
                    <span className="min-w-0 flex-1" />
                    <ClickToCall telefone={decisor.telefones[0]} leadId={lead.id}>
                      <Button variant="outline" size="sm" className="h-7 px-2 text-xs"><PhoneCall className="mr-1 h-3 w-3" />Ligar</Button>
                    </ClickToCall>
                    <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => abrirConversa(decisor.telefones[0], { novaAba: true })}><MessageSquare className="mr-1 h-3 w-3" />WhatsApp</Button>
                  </div>
                ) : (
                  <p className="mt-1.5 text-[11px] italic text-muted-foreground">sem telefone vinculado — use os números da empresa</p>
                )}
              </div>
            )}
            {demaisSocios.map((socio) => (
              <div key={socio.nome} className="rounded-lg border border-border bg-card px-3 py-2.5">
                <p className="text-[13px] font-bold uppercase text-foreground">{socio.nome}</p>
                <p className="mt-0.5 text-[11px] text-muted-foreground">{socio.cargo || 'Sócio'}{socio.cpf ? ` · CPF ${formatarCPF(socio.cpf)}` : ''}</p>
                {socio.telefones.length > 0 ? (
                  <div className="mt-1.5 flex flex-wrap items-center gap-2">
                    <span className="whitespace-nowrap text-[13.5px] font-bold tabular-nums text-foreground">{formatarTelefone(socio.telefones[0])}</span>
                    <span className="min-w-0 flex-1" />
                    <ClickToCall telefone={socio.telefones[0]} leadId={lead.id}>
                      <Button variant="outline" size="sm" className="h-7 px-2 text-xs"><PhoneCall className="mr-1 h-3 w-3" />Ligar</Button>
                    </ClickToCall>
                    <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => abrirConversa(socio.telefones[0], { novaAba: true })}><MessageSquare className="mr-1 h-3 w-3" />WhatsApp</Button>
                  </div>
                ) : (
                  <p className="mt-1.5 text-[11px] italic text-muted-foreground">sem telefone vinculado — use os números da empresa</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══ CAIXA 4 · TELEFONES ENCONTRADOS (fonte-neutra, mesma anatomia da 62a) ═══ */}
      <TelefonesEncontradosCard cnpj={lead.cnpj} onLigar={onLigar} onWhatsApp={(tel) => abrirConversa(tel, { novaAba: true })} />

      {/* ═══ CAIXA 5 · DADOS DA EMPRESA E CONTEXTO ═══ */}
      {dc && (
        <div className="overflow-hidden rounded-xl border border-border bg-card">
          <div className={HEADER38}>
            <p className={TITULO}><Building2 className="h-3.5 w-3.5 text-muted-foreground" />Dados da empresa e contexto</p>
            <span className="min-w-0 flex-1" />
            {dc.situacao_cadastral && (
              <span className={`rounded-full border px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${String(dc.situacao_cadastral).normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toUpperCase().startsWith('ATIV') ? 'border-[#075E54]/40 bg-[#075E54]/[0.06] text-[#075E54] dark:text-[#25D366]' : 'border-primary/40 bg-primary/5 text-primary dark:text-[#C87484]'}`}>
                {dc.situacao_cadastral}
              </span>
            )}
          </div>
          <div className="grid gap-x-8 p-4 md:grid-cols-2">
            {[
              ['Razão social', lead.razao_social || dc.razao_social || '—', null],
              ['CNPJ', formatarCNPJ(lead.cnpj), null],
              ['CNAE', dc.descricao_cnae ? `${dc.cod_cnae || ''} · ${dc.descricao_cnae}` : dc.cod_cnae || '—', dc.descricao_cnae || null],
              ['Porte', dc.porte_empresa || '—', null],
              ['Capital social', dc.capital_social ? formatarMoeda(dc.capital_social) : '—', null],
              ['Abertura', dc.data_abertura || '—', null],
              ['Endereço', dc.endereco ? `${dc.endereco}${dc.numero ? `, ${dc.numero}` : ''} · ${dc.cidade || ''}/${dc.uf || ''}` : '—', null],
              ['E-mail', dc.email1 || '—', null],
            ].map(([rotulo, valor, title]) => (
              <div key={String(rotulo)} className="flex items-baseline justify-between gap-4 border-b border-border py-2 text-[12.5px] last:border-b-0 md:[&:nth-last-child(2)]:border-b-0">
                <span className="flex-shrink-0 text-muted-foreground">{rotulo}</span>
                <span className="min-w-0 truncate text-right font-semibold text-foreground" title={String(title || valor)}>{valor}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* upload da fatura (Substituir) + solicitação manual */}
      <input ref={fileInputRef} type="file" accept="application/pdf,image/*" className="hidden" onChange={handleFileChange} />
      <SolicitarFaturaModal
        open={modalSolicitarManual || solicitarFaturaDialogOpen}
        onOpenChange={(o) => { setModalSolicitarManual(o); setSolicitarFaturaDialogOpen(o); }}
        razaoSocial={lead.razao_social || ''}
        linhaContaFatura={linhaEditando || melhorLinha || undefined}
        consultorId={lead.consultor_id}
        consultorNome={lead.consultor_nome}
        onSolicitarAutomatico={handleSolicitarBee360}
      />
    </div>
  );
}
